import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { FiPrinter, FiAlertCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { HolderDetailsResult, HolderMovementView, HolderActivityView } from '../../../model/HolderDetailsResult';
import { startSetStock } from '../../../actions/CurrentStock';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { displayName, initials } from '../Header';
import { providers } from './AllHolders';
import { fmt, fmtDate, holderChart, holdingColor, movementDescription, activityTitle } from './HolderDetail';
import { PERIODS, periodStart } from './HolderModals';

// /registry-report/{ticker:exchange}/holder/{holderId}?include=details,chart,movements,activity&period=all|365|90|30
// opened in a new tab from Holder detail › Export Holder Report › PDF (Figma 532:2374). Same A4 sheets as ReportView
// (ReportView.scss .registry-report-view) and the same read-only GET as Holder detail (/api/ShareRegistry/holderDetails).
// Print / Save as PDF uses the browser's print dialog.

const dash = '—';
// rows that fit a sheet (1754px tall in Figma units): the first sheet also carries the title, details and chart
const ROWS_PER_SHEET = 34;
const FIRST_SHEET_ROWS = (details: boolean, chart: boolean) => ROWS_PER_SHEET - (details ? 10 : 0) - (chart ? 9 : 0);

type Line = { kind: 'movement'; m: HolderMovementView } | { kind: 'activity'; a: HolderActivityView } | { kind: 'title'; text: string };

const HolderReportView: React.FC = () => {
    const dispatch = useDispatch();
    const { ticker, holderId } = useParams<{ ticker: string; holderId: string }>();
    const query = new URLSearchParams(useLocation().search);
    const include = (query.get('include') || 'details,chart,movements').split(',');
    const period = PERIODS[query.get('period') || ''] ? query.get('period')! : 'all';
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const loaded = !!stock && `${stock.ticker}:${stock.exchange}` === ticker;
    const [result, setResult] = useState<HolderDetailsResult | null>(null);
    const [error, setError] = useState('');

    // no header here to load the current client, so load the one in the url
    useEffect(() => {
        if (!loaded) dispatch(startSetStock(ticker));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticker]);

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!loaded) return;
        const [err, data] = await to(api().getHolderDetails(stock!.ticker, stock!.exchange, { holderId }));
        if (!isMounted()) return;
        const r = data as HolderDetailsResult | undefined;
        if (err || !r?.success || !r.holder) setError(err?.message || r?.message || 'Failed to fetch holder details');
        else setResult(r);
    }, [loaded, holderId]);

    if (!loaded) return null;

    const company = stock.companyName || stock.ticker;
    const providerName = providers[stock.shareRegistryProvider ?? 0];
    const provider = providerName && providerName !== 'None' ? providerName : 'the share registry';
    const currency = stock.exchange === 'ASX' ? 'A$' : '$';
    const from = periodStart(period);
    const fileName = `${stock.ticker}-holder-${result?.holder?.holderNumber || holderId}.pdf`;

    const toolbar = <div className="rr-toolbar">
        <span className="rr-file">{fileName}</span>
        <button type="button" className="btn-primary" onClick={() => window.print()} disabled={!result}><FiPrinter /> Print / Save as PDF</button>
    </div>;
    if (!result) return <div className="v2-page registry-report-view holder-report-view">
        {toolbar}
        {error ? <p className="error">{error}</p> : <p className="rr-loading">Loading holder details from {provider}… (first load can take a minute while reports generate)</p>}
    </div>;

    const holder = result.holder!;
    const summary = result.summary;
    const details = include.includes('details');
    const chart = include.includes('chart') && !!summary;
    const pct = result.issuedCapital && summary ? summary.currentBalance * 100 / result.issuedCapital : null;
    const { codes, option } = holderChart(result, stock.ticker, currency);
    const multiSecurity = codes.length > 1;

    // movement and activity rows, newest first, cut into sheets
    const lines: Line[] = [];
    if (include.includes('movements')) {
        const ms = [...(result.movements || [])].reverse().filter(m => m.date.slice(0, 10) >= from);
        lines.push({ kind: 'title', text: `Movement history · ${ms.length} movement${ms.length === 1 ? '' : 's'}` }, ...ms.map(m => ({ kind: 'movement' as const, m })));
    }
    if (include.includes('activity')) {
        const as = (result.activity || []).filter(a => a.date.slice(0, 10) >= from);
        lines.push({ kind: 'title', text: `Website and email activity · ${as.length} event${as.length === 1 ? '' : 's'}` }, ...as.map(a => ({ kind: 'activity' as const, a })));
    }
    const pages: Line[][] = [lines.slice(0, FIRST_SHEET_ROWS(details, chart))];
    for (let i = FIRST_SHEET_ROWS(details, chart); i < lines.length; i += ROWS_PER_SHEET) pages.push(lines.slice(i, i + ROWS_PER_SHEET));

    const label = `Holder report · ${moment().format('D MMM YYYY')}`;
    const renderLines = (ls: Line[]) => {
        const out: React.ReactNode[] = [];
        let table: React.ReactNode[] = [];
        let kind = '';
        const flush = () => {
            if (!table.length) return;
            out.push(<div key={`t${out.length}`} className={`rr-table ${kind === 'movement' ? `hr-moves${multiSecurity ? ' security' : ''}` : 'hr-activity'}`}>{table}</div>);
            table = [];
        };
        ls.forEach((l, i) => {
            if (l.kind === 'title') {
                flush();
                out.push(<h3 key={`h${i}`} className="hr-title">{l.text}</h3>);
                return;
            }
            if (l.kind !== kind) { flush(); kind = l.kind; }
            if (!table.length) table.push(l.kind === 'movement'
                ? <div key="head" className="rr-row rr-head"><span>Date</span>{multiSecurity && <span>Security</span>}<span>Description</span><span>Broker</span><span className="rr-num">Movement</span><span className="rr-num">Price</span><span className="rr-num">Closing</span></div>
                : <div key="head" className="rr-row rr-head"><span>When</span><span>What</span><span>Detail</span></div>);
            if (l.kind === 'movement') {
                const m = l.m;
                table.push(<div key={i} className="rr-row">
                    <span className="rr-strong">{fmtDate(m.date)}</span>
                    {multiSecurity && <span className="rr-sec">{m.securityCode || stock.ticker}</span>}
                    <span className="rr-sec">{movementDescription(m)}</span>
                    <span className="rr-sec">{m.broker}</span>
                    <span className={`rr-num ${m.movement >= 0 ? 'rr-up' : 'rr-down'}`}>{m.movement >= 0 ? '+' : '−'}{fmt(Math.abs(m.movement))}</span>
                    <span className="rr-num rr-sec">{m.price != null ? `${currency}${fmt(m.price, 3)}${m.priceEstimated ? '*' : ''}` : m.priceMissing ? '†' : ''}</span>
                    <span className="rr-num">{fmt(m.closingBalance)}</span>
                </div>);
            } else if (l.kind === 'activity') {
                const a = l.a;
                table.push(<div key={i} className="rr-row">
                    <span className="rr-sec">{moment(a.date).format('D MMM YYYY, h:mm a')}</span>
                    <span>{activityTitle(a)}</span>
                    <span className="rr-sec">{[a.meta, a.actions?.join(' · ').toLowerCase(), a.count && a.count > 1 ? `${a.count} visits` : null].filter(Boolean).join(' · ')}</span>
                </div>);
            }
        });
        flush();
        return out;
    };

    const sheet = (body: React.ReactNode, page: number) => <article className="rr-sheet" key={page}>
        <header className="rr-running">
            {stock.companyLogoUrl ? <img className="rr-logo" src={stock.companyLogoUrl} alt="" /> : <span className="rr-logo rr-initials">{initials(company)}</span>}
            <strong>{company.toUpperCase()}</strong>
            <span className="rr-label">{label.toUpperCase()}</span>
        </header>
        <div className="rr-rule" />
        <div className="rr-content">{body}</div>
        <footer className="rr-foot">
            <span>Registry data supplied by {provider} · Confidential · Generated {moment().format('D MMMM YYYY')} by {displayName(user)}</span>
            <strong>Page {page} of {pages.length}</strong>
        </footer>
    </article>;

    const first = <>
        <div className="rr-h2">
            <h2>{holder.name}</h2>
            <p>Holder no. {holder.holderNumber}{holder.holderType && ` · ${holder.holderType.toLowerCase()}`} · {PERIODS[period].label.toLowerCase()}</p>
        </div>
        {details && <div className="hr-details">
            <div className="rr-panel">
                <h3>Holder details</h3>
                <div className="hr-kv"><span>Address</span><span>{holder.address || dash}</span></div>
                <div className="hr-kv"><span>Email</span><span>{holder.email || dash}</span></div>
                <div className="hr-kv"><span>Phone</span><span>{holder.phone || dash}</span></div>
                <div className="hr-kv"><span>Holder ID</span><span>{holder.holderId}</span></div>
                <div className="hr-kv"><span>First invested</span><span>{fmtDate(holder.firstInvestmentDate) || dash}</span></div>
            </div>
            <div className="rr-panel">
                <h3>Position</h3>
                {summary ? <>
                    <div className="hr-big">{fmt(summary.currentBalance)}</div>
                    <div className="hr-kv"><span>% of issued capital</span><span>{pct === null ? dash : `${pct > 0 && pct < 0.01 ? '<0.01' : pct.toFixed(2)}%`}</span></div>
                    <div className="hr-kv"><span>Current price</span><span>{currency}{fmt(summary.currentPrice, 3)}</span></div>
                    <div className="hr-kv"><span>Current value</span><span>{currency}{fmt(summary.currentValue, 2)}</span></div>
                </> : <span className="rr-muted">{dash}</span>}
            </div>
        </div>}
        {chart && option && <div className="hr-chart">
            <div className="hr-legend">{codes.map((c, i) => <span key={c}><i style={{ background: holdingColor(i) }} /> {c} holding</span>)}<span><i className="cyan" /> Share price</span></div>
            <ReactECharts option={option} style={{ height: 300, width: 1060 }} notMerge={true} />
        </div>}
        {!lines.length && !details && !chart && <div className="rr-missing"><FiAlertCircle /> Nothing was picked to include.</div>}
    </>;

    const notes = (result.movements || []).some(m => m.priceEstimated || m.priceMissing) && include.includes('movements')
        ? <p className="rr-note">* price estimated from the closing price on the trade date · † no closing price found near the trade date</p> : null;

    return <div className="v2-page registry-report-view holder-report-view">
        {toolbar}
        <div className="rr-doc">
            {pages.map((ls, i) => sheet(<>{i === 0 && first}{renderLines(ls)}{i === pages.length - 1 && notes}</>, i + 1))}
        </div>
    </div>;
};

export default HolderReportView;
